import { useEffect } from 'react';
import type { UseFormSetValue } from 'react-hook-form';

import type { Order } from '~hooks/useOrders/models';

import type { OrderFormV3FieldValues } from '../OrderFormV3Context';

/**
 * Triggers: the preloads' `geofenceDelayDefault` resolving (the company's
 *           default geofence delay, fetched by useOrderFormV3Preloads).
 * Writes:   the `geofenceDelaySeconds` form field — undirtied: the seed is a
 *           company default, not a user edit.
 * Guards:   only while the mounted order carries no geofence value of its
 *           own (the preload doesn't fetch otherwise, and a saved value must
 *           never be overwritten by the company fallback).
 * Legacy:   the legacy form's geofence delay seeding — the fetch half lives
 *           in useOrderFormV3Preloads; this module is the setValue half.
 * Test:     __tests__/OrderFormV3Context.test.tsx ("geofence delay seed").
 */
export function useGeofenceDelaySeed({
  defaultOrder,
  geofenceDelayDefault,
  setValue,
}: {
  defaultOrder: Order | null;
  geofenceDelayDefault: number | null;
  setValue: UseFormSetValue<OrderFormV3FieldValues>;
}): void {
  useEffect(() => {
    if (geofenceDelayDefault !== null && !defaultOrder?.geofenceDelaySeconds) {
      setValue('geofenceDelaySeconds', geofenceDelayDefault, { shouldDirty: false });
    }
    // The order's own geofence is mount-stable and setValue is identity-
    // stable; the resolved preload is the one live trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geofenceDelayDefault]);
}
